/**
 * AI Service - Servicio para integraciones con OpenAI y Anthropic
 * Maneja las llamadas a APIs de IA para el chat asistente
 */

import { api } from './apiClient';
import type { AISettings, AIMessage, AIProvider } from '@/schemas/aiSettingsSchemas';

// Cache de configuración para evitar múltiples llamadas
let settingsCache: AISettings | null = null;
let settingsCacheTime: number = 0;
const CACHE_DURATION = 60000; // 1 minuto

// System prompt por defecto para Athenea
const DEFAULT_SYSTEM_PROMPT = `Eres un asistente de IA integrado en Athenea, un sistema MES (Manufacturing Execution System) especializado en la manufactura de composites para sectores de automoción, aeronáutica y ciclismo.

Tu rol es ayudar a los usuarios con:
- Consultas sobre el sistema y sus módulos
- Explicaciones de procesos de manufactura de composites
- Sugerencias de optimización de producción
- Interpretación de datos y KPIs
- Resolución de problemas comunes

Módulos del sistema:
- Órdenes de Trabajo: Gestión de OTs, estados, seguimiento
- Inventario: Control de materiales, prepregs, resinas, consumibles
- Producción: Programación, seguimiento de piezas, tiempos
- Calidad: Inspecciones, NCRs, certificaciones
- Mantenimiento: Máquinas, plotters, autoclaves, mantenimiento predictivo
- Clientes y Proyectos: Gestión comercial

Responde de forma concisa, profesional y en español. Si no tienes información específica, indica que el usuario debe consultar con el equipo técnico.`;

/**
 * Obtiene la configuración de IA desde la base de datos
 */
export async function getAISettings(): Promise<AISettings | null> {
  // Verificar cache
  if (settingsCache && Date.now() - settingsCacheTime < CACHE_DURATION) {
    return settingsCache;
  }

  try {
    const { data, error } = await api
      .from('ai_settings')
      .select('*')
      .eq('is_enabled', true)
      .order('created_at', { ascending: false })
      .limit(1);

    if (error) {
      console.error('Error fetching AI settings:', error);
      return null;
    }

    if (data && data.length > 0) {
      settingsCache = data[0] as AISettings;
      settingsCacheTime = Date.now();
      return settingsCache;
    }

    return null;
  } catch (error) {
    console.error('Error in getAISettings:', error);
    return null;
  }
}

/**
 * Guarda o actualiza la configuración de IA
 */
export async function saveAISettings(settings: Partial<AISettings>): Promise<{ success: boolean; error?: string }> {
  try {
    // Verificar si ya existe una configuración
    const { data: existing } = await api
      .from('ai_settings')
      .select('id')
      .limit(1);

    if (existing && existing.length > 0) {
      // Actualizar existente
      const { error } = await api
        .from('ai_settings')
        .update({
          ...settings,
          updated_at: new Date().toISOString(),
        })
        .eq('id', existing[0].id);

      if (error) throw error;
    } else {
      // Crear nuevo
      const { error } = await api
        .from('ai_settings')
        .insert({
          ...settings,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        });

      if (error) throw error;
    }

    // Invalidar cache
    settingsCache = null;
    settingsCacheTime = 0;

    return { success: true };
  } catch (error: any) {
    console.error('Error saving AI settings:', error);
    return { success: false, error: error.message || 'Error desconocido' };
  }
}

/**
 * Envía un mensaje al modelo de IA y obtiene la respuesta
 */
export async function sendMessage(
  messages: AIMessage[],
  settings?: AISettings
): Promise<{ content: string; error?: string }> {
  const aiSettings = settings || await getAISettings();

  if (!aiSettings) {
    return { content: '', error: 'No hay configuración de IA activa. Configure la API key en Ajustes.' };
  }

  const systemPrompt = aiSettings.system_prompt || DEFAULT_SYSTEM_PROMPT;
  const fullMessages: AIMessage[] = [
    { role: 'system', content: systemPrompt },
    ...messages,
  ];

  try {
    if (aiSettings.provider === 'openai') {
      return await sendOpenAIMessage(fullMessages, aiSettings);
    } else {
      return await sendAnthropicMessage(fullMessages, aiSettings);
    }
  } catch (error: any) {
    console.error('Error sending message:', error);
    return { content: '', error: error.message || 'Error al comunicar con la IA' };
  }
}

/**
 * Envía mensaje a OpenAI
 */
async function sendOpenAIMessage(
  messages: AIMessage[],
  settings: AISettings
): Promise<{ content: string; error?: string }> {
  const response = await fetch('https://api.openai.com/v1/chat/completions', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${settings.api_key}`,
    },
    body: JSON.stringify({
      model: settings.model,
      messages: messages.map(m => ({ role: m.role, content: m.content })),
      max_tokens: settings.max_tokens,
      temperature: settings.temperature,
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.error?.message || `Error HTTP ${response.status}`;
    throw new Error(errorMessage);
  }

  const data = await response.json();
  const content = data.choices?.[0]?.message?.content || '';

  return { content };
}

/**
 * Envía mensaje a Anthropic
 */
async function sendAnthropicMessage(
  messages: AIMessage[],
  settings: AISettings
): Promise<{ content: string; error?: string }> {
  // Separar system message del resto
  const systemMessage = messages.find(m => m.role === 'system');
  const conversationMessages = messages.filter(m => m.role !== 'system');

  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': settings.api_key,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model: settings.model,
      max_tokens: settings.max_tokens,
      system: systemMessage?.content || '',
      messages: conversationMessages.map(m => ({ role: m.role, content: m.content })),
    }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    const errorMessage = errorData.error?.message || `Error HTTP ${response.status}`;
    throw new Error(errorMessage);
  }

  const data = await response.json();
  const content = data.content?.[0]?.text || '';

  return { content };
}

/**
 * Prueba la conexión con la API de IA
 */
export async function testConnection(settings: AISettings): Promise<{ success: boolean; error?: string }> {
  try {
    const testMessages: AIMessage[] = [
      { role: 'user', content: 'Responde únicamente con la palabra "OK" para confirmar conexión.' }
    ];

    const result = await sendMessage(testMessages, settings);

    if (result.error) {
      return { success: false, error: result.error };
    }

    return { success: true };
  } catch (error: any) {
    return { success: false, error: error.message || 'Error de conexión' };
  }
}

/**
 * Limpia el cache de configuración
 */
export function clearSettingsCache(): void {
  settingsCache = null;
  settingsCacheTime = 0;
}
